/* Apex Pass, browser side. The Pass is the Apex ID: one sign-in for every Apex
 * store and the Arcade. The token lives in localStorage ("apex-pass", shared
 * with the Arcade Play window) and in a first-party cookie so this store's
 * checkout routes can apply member prices. */

import { BACKEND_BASE } from "@/lib/backend";

export const PASS_KEY = "apex-pass";
export const PASS_COOKIE = "apex_pass";
export const PASS_EVENT = "apex-pass-change";
const API = `${BACKEND_BASE}/api/v1`;

export type PassMe = {
  signedIn: boolean;
  email: string | null;
  member: boolean;
  status?: string | null;
  renewsAt?: string | null;
  cancelAtPeriodEnd?: boolean;
  usedDiscount?: boolean;
  price: number;
  digitalDiscount: number;
};

export function getToken(): string | null {
  try {
    const v = JSON.parse(localStorage.getItem(PASS_KEY) || "null") as { token?: string } | null;
    return v?.token ?? null;
  } catch {
    return null;
  }
}

function writeCookie(token: string | null) {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = token
    ? `${PASS_COOKIE}=${encodeURIComponent(token)}; Path=/; Max-Age=2592000; SameSite=Lax${secure}`
    : `${PASS_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
}

export function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(PASS_KEY, JSON.stringify({ token }));
    else localStorage.removeItem(PASS_KEY);
  } catch { /* storage blocked: the cookie still carries it for checkout */ }
  writeCookie(token);
  dispatchEvent(new Event(PASS_EVENT));
}

/* Keep the cookie in step when the Arcade Play window signs the visitor in. */
export function syncCookie() {
  writeCookie(getToken());
}

async function call<T>(path: string, init: RequestInit = {}): Promise<{ ok: boolean; status: number; data: T | null; error: string }> {
  const token = getToken();
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (token) headers.authorization = `Bearer ${token}`;
  try {
    const res = await fetch(`${API}${path}`, { ...init, headers, credentials: "omit" });
    const data = (await res.json().catch(() => null)) as (T & { error?: { message?: string } }) | null;
    return { ok: res.ok, status: res.status, data, error: res.ok ? "" : data?.error?.message || "Something went wrong. Try again." };
  } catch {
    return { ok: false, status: 0, data: null, error: "Can't reach the Apex Pass right now. Try again in a moment." };
  }
}

export async function fetchMe(): Promise<PassMe | null> {
  const r = await call<PassMe>("/pass/me");
  if (r.status === 401) { setToken(null); return null; }
  return r.data;
}

export async function signIn(email: string, password: string) {
  const r = await call<{ token?: string }>("/id/signin", { method: "POST", body: JSON.stringify({ email, password }) });
  if (r.ok && r.data?.token) setToken(r.data.token);
  return { ok: r.ok && Boolean(r.data?.token), error: r.error };
}

export async function signUp(email: string, password: string, over13: boolean) {
  const r = await call<{ token?: string; confirm?: boolean; message?: string }>("/id/signup", { method: "POST", body: JSON.stringify({ email, password, over13 }) });
  if (r.ok && r.data?.token) setToken(r.data.token);
  return { ok: r.ok, confirm: Boolean(r.data?.confirm), message: r.data?.message ?? "", error: r.error };
}

export function signOut() {
  setToken(null);
}

export async function startMembership(): Promise<{ url?: string; error?: string }> {
  const back = `${location.origin}/apex-pass`;
  const r = await call<{ checkout_url?: string }>("/pass/checkout", {
    method: "POST",
    body: JSON.stringify({ success_url: `${back}?joined=1`, cancel_url: back }),
  });
  return r.ok && r.data?.checkout_url ? { url: r.data.checkout_url } : { error: r.error };
}

export async function setCancel(cancel: boolean) {
  const r = await call<{ ok?: boolean }>("/pass/cancel", { method: "POST", body: JSON.stringify({ resume: !cancel }) });
  return { ok: r.ok, error: r.error };
}
